import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button, Avatar,
  Divider, Chip, Alert, CircularProgress, IconButton, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem,
  ListItemIcon, ListItemText, Switch, alpha, Paper, Tabs, Tab, Tooltip,
  Table, TableBody, TableRow, TableCell
} from '@mui/material';
import {
  Person, Lock, Security, Edit, Save, Visibility, VisibilityOff,
  Shield, PhoneAndroid, ContentCopy, Warning, CheckCircle, History,
  Email, Phone, Badge, CalendarToday, VpnKey, DevicesOther, Close
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { profileAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';


const Profile = () => {
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile fields
  const [profile, setProfile] = useState({
    firstName: '', lastName: '', email: '', phone: '', username: '', role: ''
  });
  const [editMode, setEditMode] = useState(false);

  // Password fields
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordStrength, setPasswordStrength] = useState(0);

  // MFA
  const [securityInfo, setSecurityInfo] = useState(null);
  const [mfaSetup, setMfaSetup] = useState(null);
  const [mfaToken, setMfaToken] = useState('');
  const [mfaBackupCodes, setMfaBackupCodes] = useState(null);
  const [disableMfaOpen, setDisableMfaOpen] = useState(false);
  const [disableMfaPassword, setDisableMfaPassword] = useState('');

  useEffect(() => { document.title = 'Profile | MyPayPlus'; }, []);
  useEffect(() => {
    loadProfile();
    loadSecurity();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await profileAPI.get();
      const u = res.data;
      setProfile({
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        phone: u.phone || '',
        username: u.username || '',
        role: u.role || ''
      });
    } catch {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadSecurity = async () => {
    try {
      const res = await profileAPI.getSecurity();
      setSecurityInfo(res.data);
    } catch (err) {
      console.error('Failed to load security info:', err);
      // Fallback: use AuthContext user data to determine MFA status
      if (user) {
        setSecurityInfo(prev => ({
          ...prev,
          mfaEnabled: !!(user.totpEnabled || user.mfaEnabled),
          lastLogin: user.lastLogin,
          accountCreated: user.createdAt
        }));
      }
    }
  };

  const handleProfileSave = async () => {
    setSaving(true);
    try {
      const res = await profileAPI.update({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
        phone: profile.phone
      });
      toast.success('Profile updated successfully');
      setEditMode(false);
      // Refresh user in context so header updates immediately
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (password.length >= 12) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    setPasswordStrength(strength);
  };

  const handlePasswordChange = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setSaving(true);
    try {
      await profileAPI.changePassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword
      });
      toast.success('Password changed successfully');
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordStrength(0);
      loadSecurity();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to change password');
    } finally {
      setSaving(false);
    }
  };

  const handleMfaSetup = async () => {
    try {
      const res = await profileAPI.mfaSetup();
      setMfaSetup(res.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to start MFA setup');
      // If MFA is already enabled, refresh status so UI updates
      await loadSecurity();
      await refreshUser();
    }
  };

  const handleMfaVerify = async () => {
    if (!mfaToken || mfaToken.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    try {
      const res = await profileAPI.mfaVerify({ token: mfaToken });
      setMfaBackupCodes(res.data.backupCodes);
      setMfaSetup(null);
      setMfaToken('');
      await loadSecurity();
      await refreshUser();
      toast.success('MFA enabled successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid verification code');
    }
  };

  const handleMfaDisable = async () => {
    try {
      await profileAPI.mfaDisable({ password: disableMfaPassword });
      setDisableMfaOpen(false);
      setDisableMfaPassword('');
      await loadSecurity();
      await refreshUser();
      toast.success('MFA has been disabled');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to disable MFA');
    }
  };

  const copyBackupCodes = () => {
    if (mfaBackupCodes) {
      navigator.clipboard.writeText(mfaBackupCodes.join('\n'));
      toast.success('Backup codes copied to clipboard');
    }
  };

  const strengthLabel = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  const strengthColor = ['#ef4444', '#f59e0b', '#f59e0b', '#10b981', '#059669'];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 0.5 }}>My Profile</Typography>
        <Typography variant="body2" color="text.secondary">
          Manage your account settings, security, and preferences
        </Typography>
      </Box>

      {/* Profile Banner */}
      <Card sx={{ mb: 3, overflow: 'visible', position: 'relative' }}>
        <Box sx={{
          height: 120,
          background: user?.isSuperAdmin
            ? 'linear-gradient(135deg, #dc2626 0%, #f97316 100%)'
            : 'linear-gradient(135deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)',
          borderRadius: '16px 16px 0 0'
        }} />
        <Box sx={{ px: 3, pb: 3, mt: -5, display: 'flex', alignItems: 'flex-end', gap: 2.5, flexWrap: 'wrap' }}>
          <Avatar sx={{
            width: 88, height: 88, fontSize: 32, fontWeight: 800,
            border: '4px solid #fff',
            boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
            background: user?.isSuperAdmin
              ? 'linear-gradient(135deg, #dc2626, #f97316)'
              : 'linear-gradient(135deg, #3b82f6, #6366f1)'
          }}>
            {profile.firstName?.[0]}{profile.lastName?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 200, pt: 5 }}>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              {profile.firstName} {profile.lastName}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip
                label={(profile.role || '').replace(/_/g, ' ').toUpperCase()}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: '0.7rem',
                  bgcolor: user?.isSuperAdmin ? alpha('#dc2626', 0.1) : alpha('#3b82f6', 0.1),
                  color: user?.isSuperAdmin ? '#dc2626' : '#3b82f6'
                }}
              />
              <Typography variant="body2" color="text.secondary">
                @{profile.username}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ pt: 5 }}>
            {securityInfo?.mfaEnabled && (
              <Chip icon={<Shield sx={{ fontSize: 16 }} />} label="MFA Enabled" size="small"
                sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669', fontWeight: 600, fontSize: '0.75rem' }} />
            )}
          </Box>
        </Box>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            px: 2, borderBottom: '1px solid', borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600, fontSize: '0.85rem', textTransform: 'none', minHeight: 52 }
          }}
        >
          <Tab icon={<Person sx={{ fontSize: 18 }} />} iconPosition="start" label="Profile" />
          <Tab icon={<Lock sx={{ fontSize: 18 }} />} iconPosition="start" label="Password" />
          <Tab icon={<Security sx={{ fontSize: 18 }} />} iconPosition="start" label="Multi-Factor Auth" />
          <Tab icon={<History sx={{ fontSize: 18 }} />} iconPosition="start" label="Account Security" />
        </Tabs>

        <CardContent sx={{ p: 3 }}>
          {/* ========== TAB 0: Profile Info ========== */}
          {activeTab === 0 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6">Personal Information</Typography>
                  <Typography variant="body2" color="text.secondary">Update your personal details</Typography>
                </Box>
                {!editMode ? (
                  <Button startIcon={<Edit />} variant="outlined" onClick={() => setEditMode(true)}>
                    Edit Profile
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" color="inherit" onClick={() => { setEditMode(false); loadProfile(); }}>
                      Cancel
                    </Button>
                    <Button startIcon={<Save />} variant="contained" onClick={handleProfileSave} disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                )}
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="First Name"
                    value={profile.firstName}
                    onChange={(e) => setProfile({ ...profile, firstName: e.target.value })}
                    fullWidth disabled={!editMode}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Badge sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Last Name"
                    value={profile.lastName}
                    onChange={(e) => setProfile({ ...profile, lastName: e.target.value })}
                    fullWidth disabled={!editMode}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Badge sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Email Address"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    fullWidth disabled={!editMode}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Phone Number"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    fullWidth disabled={!editMode}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Username" value={profile.username} fullWidth disabled
                    helperText="Username cannot be changed"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Person sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField label="Role" value={(profile.role || '').replace(/_/g, ' ')} fullWidth disabled
                    helperText="Contact an admin to change your role"
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><VpnKey sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>
                    }}
                    sx={{ '& input': { textTransform: 'capitalize' } }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ========== TAB 1: Password ========== */}
          {activeTab === 1 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">Change Password</Typography>
                <Typography variant="body2" color="text.secondary">
                  Ensure your account stays secure by using a strong password
                </Typography>
              </Box>

              {securityInfo?.passwordChangedAt && (
                <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
                  Last password change: {new Date(securityInfo.passwordChangedAt).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  })}
                </Alert>
              )}

              <Grid container spacing={3} sx={{ maxWidth: 500 }}>
                <Grid item xs={12}>
                  <TextField
                    label="Current Password"
                    type={showPasswords.current ? 'text' : 'password'}
                    value={passwords.currentPassword}
                    onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><Lock sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}>
                            {showPasswords.current ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="New Password"
                    type={showPasswords.new ? 'text' : 'password'}
                    value={passwords.newPassword}
                    onChange={(e) => {
                      setPasswords({ ...passwords, newPassword: e.target.value });
                      checkPasswordStrength(e.target.value);
                    }}
                    fullWidth
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><VpnKey sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}>
                            {showPasswords.new ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  {passwords.newPassword && (
                    <Box sx={{ mt: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                        {[0, 1, 2, 3, 4].map(i => (
                          <Box key={i} sx={{
                            flex: 1, height: 4, borderRadius: 2,
                            bgcolor: i < passwordStrength ? strengthColor[passwordStrength - 1] : '#e2e8f0',
                            transition: 'background-color 0.3s'
                          }} />
                        ))}
                      </Box>
                      <Typography variant="caption" sx={{ color: strengthColor[passwordStrength - 1] || '#94a3b8' }}>
                        {passwordStrength > 0 ? strengthLabel[passwordStrength - 1] : 'Enter a password'}
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Confirm New Password"
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={passwords.confirmPassword}
                    onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
                    fullWidth
                    error={passwords.confirmPassword !== '' && passwords.newPassword !== passwords.confirmPassword}
                    helperText={passwords.confirmPassword !== '' && passwords.newPassword !== passwords.confirmPassword ? 'Passwords do not match' : ''}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><VpnKey sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}>
                            {showPasswords.confirm ? <VisibilityOff sx={{ fontSize: 18 }} /> : <Visibility sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained" onClick={handlePasswordChange} disabled={saving || !passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword}
                    startIcon={<Lock />}
                  >
                    {saving ? 'Changing...' : 'Change Password'}
                  </Button>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }} />

              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Password Requirements</Typography>
                <List dense>
                  {[
                    { text: 'Minimum 8 characters', met: passwords.newPassword.length >= 8 },
                    { text: 'At least one uppercase letter', met: /[A-Z]/.test(passwords.newPassword) },
                    { text: 'At least one number', met: /[0-9]/.test(passwords.newPassword) },
                    { text: 'At least one special character', met: /[^A-Za-z0-9]/.test(passwords.newPassword) },
                  ].map((req, idx) => (
                    <ListItem key={idx} sx={{ py: 0.3, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircle sx={{
                          fontSize: 16,
                          color: passwords.newPassword ? (req.met ? '#10b981' : '#e2e8f0') : '#e2e8f0'
                        }} />
                      </ListItemIcon>
                      <ListItemText primary={req.text} primaryTypographyProps={{
                        fontSize: '0.85rem',
                        color: passwords.newPassword ? (req.met ? '#10b981' : '#94a3b8') : '#94a3b8'
                      }} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}

          {/* ========== TAB 2: MFA ========== */}
          {activeTab === 2 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">Multi-Factor Authentication</Typography>
                <Typography variant="body2" color="text.secondary">
                  Add an extra layer of security to your account using an authenticator app
                </Typography>
              </Box>

              {/* MFA Status Card */}
              <Paper sx={{
                p: 3, mb: 3, borderRadius: 3,
                border: '1px solid',
                borderColor: securityInfo?.mfaEnabled ? alpha('#10b981', 0.3) : alpha('#f59e0b', 0.3),
                bgcolor: securityInfo?.mfaEnabled ? alpha('#10b981', 0.04) : alpha('#f59e0b', 0.04)
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 48, height: 48, borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    bgcolor: securityInfo?.mfaEnabled ? alpha('#10b981', 0.15) : alpha('#f59e0b', 0.15)
                  }}>
                    {securityInfo?.mfaEnabled
                      ? <Shield sx={{ fontSize: 24, color: '#10b981' }} />
                      : <Warning sx={{ fontSize: 24, color: '#f59e0b' }} />
                    }
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {securityInfo?.mfaEnabled ? 'MFA is Enabled' : 'MFA is Not Enabled'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {securityInfo?.mfaEnabled
                        ? 'Your account is protected with two-factor authentication'
                        : 'Enable MFA to add an extra layer of security to your account'
                      }
                    </Typography>
                  </Box>
                  {securityInfo?.mfaEnabled ? (
                    <Button variant="outlined" color="error" onClick={() => setDisableMfaOpen(true)}>
                      Disable MFA
                    </Button>
                  ) : (
                    <Button variant="contained" startIcon={<PhoneAndroid />} onClick={handleMfaSetup}
                      disabled={!!mfaSetup}>
                      Set Up MFA
                    </Button>
                  )}
                </Box>
              </Paper>

              {/* MFA Setup Flow */}
              {mfaSetup && !mfaBackupCodes && (
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Step 1: Scan QR Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <Box sx={{
                      p: 2, bgcolor: '#fff', borderRadius: 3, border: '1px solid #e2e8f0',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                    }}>
                      <img src={mfaSetup.qrCode} alt="MFA QR Code" style={{ width: 200, height: 200 }} />
                    </Box>
                  </Box>

                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Can't scan the QR code?</Typography>
                    <Typography variant="body2">Manual entry key: <strong style={{ fontFamily: 'monospace', letterSpacing: 2 }}>{mfaSetup.secret}</strong></Typography>
                  </Alert>

                  <Divider sx={{ my: 3 }} />

                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                    Step 2: Enter Verification Code
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Enter the 6-digit code from your authenticator app
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <TextField
                      value={mfaToken}
                      onChange={(e) => setMfaToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      inputProps={{
                        maxLength: 6,
                        style: { fontSize: '1.5rem', letterSpacing: 8, textAlign: 'center', fontWeight: 700, fontFamily: 'monospace' }
                      }}
                      sx={{ width: 220 }}
                    />
                    <Button variant="contained" onClick={handleMfaVerify} disabled={mfaToken.length !== 6}
                      sx={{ height: 42 }}>
                      Verify & Enable
                    </Button>
                    <Button variant="outlined" color="inherit" onClick={() => { setMfaSetup(null); setMfaToken(''); }}
                      sx={{ height: 42 }}>
                      Cancel
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* Backup Codes */}
              {mfaBackupCodes && (
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: alpha('#f59e0b', 0.3), bgcolor: alpha('#f59e0b', 0.04) }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Warning sx={{ color: '#f59e0b' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Save Your Backup Codes</Typography>
                  </Box>
                  <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
                    Store these codes in a safe place. You can use them to access your account if you lose your authenticator device.
                    These codes will only be shown once.
                  </Alert>
                  <Box sx={{
                    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mb: 2,
                    p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0'
                  }}>
                    {mfaBackupCodes.map((code, idx) => (
                      <Box key={idx} sx={{
                        py: 1, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700,
                        fontSize: '0.9rem', color: '#0f172a', letterSpacing: 1
                      }}>
                        {code}
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button startIcon={<ContentCopy />} variant="outlined" onClick={copyBackupCodes}>
                      Copy Codes
                    </Button>
                    <Button variant="contained" onClick={() => setMfaBackupCodes(null)}>
                      I've Saved My Codes
                    </Button>
                  </Box>
                </Paper>
              )}

              {/* How it works */}
              {!mfaSetup && !mfaBackupCodes && (
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>How MFA Works</Typography>
                  <List>
                    {[
                      { icon: <PhoneAndroid />, primary: 'Install an Authenticator App', secondary: 'Download Google Authenticator, Authy, or Microsoft Authenticator on your phone' },
                      { icon: <Security />, primary: 'Scan the QR Code', secondary: 'Use your authenticator app to scan the QR code we provide' },
                      { icon: <Shield />, primary: 'Enter Verification Code', secondary: 'Type the 6-digit code from your app to complete setup' },
                      { icon: <CheckCircle />, primary: 'Stay Protected', secondary: 'Each time you sign in, you\'ll enter a code from your authenticator app' },
                    ].map((item, idx) => (
                      <ListItem key={idx} sx={{ px: 0 }}>
                        <ListItemIcon>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha('#3b82f6', 0.1)
                          }}>
                            {React.cloneElement(item.icon, { sx: { fontSize: 18, color: '#3b82f6' } })}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={item.primary}
                          secondary={item.secondary}
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}

          {/* ========== TAB 3: Account Security ========== */}
          {activeTab === 3 && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6">Account Security</Typography>
                <Typography variant="body2" color="text.secondary">
                  Review your account security status and recent activity
                </Typography>
              </Box>

              {/* Security Overview */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  {
                    label: 'MFA Status',
                    value: securityInfo?.mfaEnabled ? 'Enabled' : 'Disabled',
                    icon: <Shield />,
                    color: securityInfo?.mfaEnabled ? '#10b981' : '#f59e0b',
                    bgColor: securityInfo?.mfaEnabled ? alpha('#10b981', 0.1) : alpha('#f59e0b', 0.1)
                  },
                  {
                    label: 'Last Login',
                    value: securityInfo?.lastLogin
                      ? new Date(securityInfo.lastLogin).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'N/A',
                    icon: <CalendarToday />,
                    color: '#3b82f6',
                    bgColor: alpha('#3b82f6', 0.1)
                  },
                  {
                    label: 'Password Changed',
                    value: securityInfo?.passwordChangedAt
                      ? new Date(securityInfo.passwordChangedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Never',
                    icon: <Lock />,
                    color: securityInfo?.passwordChangedAt ? '#10b981' : '#f59e0b',
                    bgColor: securityInfo?.passwordChangedAt ? alpha('#10b981', 0.1) : alpha('#f59e0b', 0.1)
                  },
                  {
                    label: 'Account Created',
                    value: securityInfo?.accountCreated
                      ? new Date(securityInfo.accountCreated).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'N/A',
                    icon: <Person />,
                    color: '#8b5cf6',
                    bgColor: alpha('#8b5cf6', 0.1)
                  }
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Paper sx={{
                      p: 2.5, borderRadius: 3, border: '1px solid #e2e8f0',
                      display: 'flex', alignItems: 'center', gap: 2
                    }}>
                      <Box sx={{
                        width: 42, height: 42, borderRadius: 2.5, display: 'flex',
                        alignItems: 'center', justifyContent: 'center', bgcolor: item.bgColor
                      }}>
                        {React.cloneElement(item.icon, { sx: { fontSize: 20, color: item.color } })}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {item.label}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {item.value}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Security Recommendations */}
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>Security Recommendations</Typography>
                <List>
                  {[
                    {
                      text: 'Enable Multi-Factor Authentication',
                      description: 'Protect your account with a second verification step',
                      done: securityInfo?.mfaEnabled,
                      action: () => setActiveTab(2)
                    },
                    {
                      text: 'Change password regularly',
                      description: 'Update your password at least every 90 days',
                      done: securityInfo?.passwordChangedAt &&
                        (new Date() - new Date(securityInfo.passwordChangedAt)) < 90 * 24 * 60 * 60 * 1000,
                      action: () => setActiveTab(1)
                    },
                    {
                      text: 'Use a strong password',
                      description: 'Include uppercase, lowercase, numbers, and special characters',
                      done: true // We can't check this without the actual password
                    }
                  ].map((item, idx) => (
                    <ListItem key={idx} sx={{
                      px: 2, py: 1.5, borderRadius: 2, mb: 0.5,
                      bgcolor: item.done ? alpha('#10b981', 0.04) : alpha('#f59e0b', 0.04),
                      cursor: item.action ? 'pointer' : 'default',
                      '&:hover': item.action ? { bgcolor: item.done ? alpha('#10b981', 0.08) : alpha('#f59e0b', 0.08) } : {}
                    }}
                      onClick={item.action}
                    >
                      <ListItemIcon>
                        {item.done
                          ? <CheckCircle sx={{ color: '#10b981' }} />
                          : <Warning sx={{ color: '#f59e0b' }} />
                        }
                      </ListItemIcon>
                      <ListItemText
                        primary={item.text}
                        secondary={item.description}
                        primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                      {item.done
                        ? <Chip label="Complete" size="small" sx={{ bgcolor: alpha('#10b981', 0.1), color: '#059669', fontWeight: 600, fontSize: '0.72rem' }} />
                        : <Chip label="Action Needed" size="small" sx={{ bgcolor: alpha('#f59e0b', 0.1), color: '#d97706', fontWeight: 600, fontSize: '0.72rem' }} />
                      }
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Recent Login Activity */}
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid #e2e8f0', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Recent Login Activity</Typography>
                  <Button size="small" color="error" variant="outlined"
                    startIcon={<DevicesOther />}
                    onClick={async () => {
                      try {
                        await profileAPI.clearSessions();
                        toast.success('Sessions cleared');
                        loadSecurity();
                      } catch {
                        toast.error('Failed to clear sessions');
                      }
                    }}
                  >
                    Clear All Sessions
                  </Button>
                </Box>
                {securityInfo?.recentLogins?.length > 0 ? (
                  <Table size="small">
                    <TableBody>
                      {securityInfo.recentLogins.map((entry, idx) => (
                        <TableRow key={idx}>
                          <TableCell sx={{ borderColor: '#f1f5f9' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <DevicesOther sx={{ fontSize: 16, color: '#94a3b8' }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {entry.userAgent ? (entry.userAgent.length > 50 ? entry.userAgent.substring(0, 50) + '...' : entry.userAgent) : 'Unknown Device'}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderColor: '#f1f5f9' }}>
                            <Typography variant="body2" color="text.secondary">{entry.ip || 'Unknown IP'}</Typography>
                          </TableCell>
                          <TableCell sx={{ borderColor: '#f1f5f9' }}>
                            <Typography variant="body2" color="text.secondary">
                              {new Date(entry.date).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <History sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">No recent login activity recorded</Typography>
                  </Box>
                )}
              </Paper>

              {/* Danger Zone */}
              <Paper sx={{
                p: 3, borderRadius: 3,
                border: '1px solid', borderColor: alpha('#ef4444', 0.2),
                bgcolor: alpha('#ef4444', 0.02)
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ef4444', mb: 1 }}>
                  Danger Zone
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  These actions are irreversible. Please proceed with caution.
                </Typography>
                <Button variant="outlined" color="error" size="small"
                  onClick={async () => {
                    try {
                      await profileAPI.clearSessions();
                      toast.success('All sessions invalidated');
                      loadSecurity();
                    } catch {
                      toast.error('Failed to clear sessions');
                    }
                  }}>
                  Sign Out All Other Devices
                </Button>
              </Paper>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Disable MFA Dialog */}
      <Dialog open={disableMfaOpen} onClose={() => setDisableMfaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning sx={{ color: '#ef4444' }} />
            Disable Multi-Factor Authentication
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
            Disabling MFA will make your account less secure. You'll only need your password to sign in.
          </Alert>
          <TextField
            label="Confirm your password"
            type="password"
            value={disableMfaPassword}
            onChange={(e) => setDisableMfaPassword(e.target.value)}
            fullWidth
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setDisableMfaOpen(false); setDisableMfaPassword(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleMfaDisable} disabled={!disableMfaPassword}>
            Disable MFA
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Profile;
